import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import { PanelLeft, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "../button";
import { ShortcutKeys } from "../shortcut-keys";
import { Skeleton } from "../skeleton";
import { useSidebar } from "../sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip";
import { AppHeaderBar } from "./app-header-bar";
import { HeaderIconPlaceholder, PanelTogglePlaceholder } from "./header-icon-button";
import { HeaderTitle } from "./header-title";
const COLLAPSED_CLASS = "group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:px-0";
interface AppSidebarHeaderProps {
  title: ReactNode;
  actions?: ReactNode;
  /** Inside a shadcn `Sidebar` (`group` + `data-state`): hides the title and
   * centers the actions when it collapses to icons. */
  collapsible?: boolean;
  className?: string;
}

/**
 * Header row of a contextual sidebar (GitHub Actions, Settings, [code]smith):
 * title on the left, icon controls flush right.
 */
export function AppSidebarHeader(t0) {
  const $ = _c(14);
  const {
    title,
    actions,
    collapsible: t1,
    className
  } = t0;
  const collapsible = t1 === undefined ? false : t1;
  const t2 = collapsible && COLLAPSED_CLASS;
  let t3;
  if ($[0] !== className || $[1] !== t2) {
    t3 = cn("justify-between", t2, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = collapsible && "group-data-[state=collapsed]:hidden";
  let t5;
  if ($[3] !== t4) {
    t5 = cn(t4);
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t5 || $[6] !== title) {
    t6 = <HeaderTitle as="span" size="m" className={t5}>{title}</HeaderTitle>;
    $[5] = t5;
    $[6] = title;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== actions) {
    t7 = <div className="flex items-center gap-1">{actions}</div>;
    $[8] = actions;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t3 || $[11] !== t6 || $[12] !== t7) {
    t8 = <AppHeaderBar inset="sidebar" className={t3}>{t6}{t7}</AppHeaderBar>;
    $[10] = t3;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
}
const SIDEBAR_ACTION_BUTTON_CLASS = "h-8 w-8 rounded-lg p-1.5 text-neutral-500 hover:bg-black/[0.04] hover:text-neutral-900 hover:!border-transparent dark:text-neutral-400 dark:hover:bg-white/[0.04] dark:hover:text-white dark:hover:!border-transparent cursor-pointer";

/** Command-palette search (hidden when collapsed) + sidebar toggle, wired to the
 * shadcn `Sidebar` the header sits in. */
export function AppSidebarHeaderActions() {
  const $ = _c(11);
  const {
    toggleSidebar
  } = useSidebar();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = cn(SIDEBAR_ACTION_BUTTON_CLASS, "group-data-[state=collapsed]:hidden");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <TooltipTrigger asChild={true}><Button variant="subtle" size="icon" className={t0} aria-label="Search" onClick={_temp}><Search className="size-4" /></Button></TooltipTrigger>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span>Search</span>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Tooltip disableHoverableContent={true}>{t1}<TooltipContent side="right">{t2}<ShortcutKeys keys={["\u2318", "K"]} className="ml-2" /></TooltipContent></Tooltip>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <PanelLeft className="size-4" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== toggleSidebar) {
    t5 = <TooltipTrigger asChild={true}><Button variant="subtle" size="icon" className={SIDEBAR_ACTION_BUTTON_CLASS} aria-label="Toggle sidebar" onClick={toggleSidebar}>{t4}</Button></TooltipTrigger>;
    $[5] = toggleSidebar;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span>Toggle sidebar</span>;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <TooltipContent side="right">{t6}<ShortcutKeys keys={["\u2318", "B"]} className="ml-2" /></TooltipContent>;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t5) {
    t8 = <>{t3}<Tooltip disableHoverableContent={true}>{t5}{t7}</Tooltip></>;
    $[9] = t5;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  return t8;
}

/**
 * Resting-state twin of `AppSidebarHeader` + `AppSidebarHeaderActions` for the
 * contextual-sidebar skeletons: same geometry, glyphs at their idle color.
 */
function _temp() {
  document.dispatchEvent(new KeyboardEvent("keydown", {
    key: "k",
    metaKey: true,
    bubbles: true,
    cancelable: true
  }));
}
export function AppSidebarHeaderSkeleton(t0) {
  const $ = _c(6);
  const {
    title,
    collapsed: t1
  } = t0;
  const collapsed = t1 === undefined ? false : t1;
  if (collapsed) {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <AppHeaderBar className="justify-center"><PanelTogglePlaceholder size="md" className="text-neutral-500" /></AppHeaderBar>;
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    return t2;
  }
  let t2;
  if ($[1] !== title) {
    t2 = title ? <HeaderTitle as="span" size="m">{title}</HeaderTitle> : <Skeleton className="h-4 w-24 rounded" />;
    $[1] = title;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex items-center gap-1"><HeaderIconPlaceholder icon={Search} size="md" className="text-neutral-500" /><PanelTogglePlaceholder size="md" className="text-neutral-500" /></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2) {
    t4 = <AppHeaderBar inset="sidebar" className="justify-between">{t2}{t3}</AppHeaderBar>;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}